import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAppUser } from '../hooks/useAppUser';
import { FamilyProfile } from '../types/familyProfile';

interface OnboardingContextType {
  hasCompletedOnboarding: boolean | null;
  loading: boolean;
  completeOnboarding: (profile: FamilyProfile) => Promise<void>;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

// Onboarding status is one boolean off the user profile, and useAppUser
// already fetches (and dedupes) that profile once per navigation — so this
// provider derives from it instead of issuing its own /api/user/profile
// fetch. The old mount-time fetch here ran concurrently with useAppUser's,
// doubling the profile request on every load without the localStorage flag.
export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading: profileFetchLoading, profileLoaded } = useAppUser();
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  // profileLoaded only flips on a successful fetch, so a failed fetch
  // presents as "not loading and not loaded" — but that is also the very
  // first render's state, before useAppUser's fetch effect has started.
  // Only conclude failure after the fetch has actually been seen in flight.
  const sawProfileFetchRef = useRef(false);

  useEffect(() => {
    if (!user) {
      sawProfileFetchRef.current = false;
      setHasCompletedOnboarding(null);
      setLoading(false);
      return;
    }
    if (profileFetchLoading) {
      sawProfileFetchRef.current = true;
    }

    // localStorage fast path, as before: answers instantly on repeat loads
    // without waiting for the profile fetch to resolve.
    const cached = localStorage.getItem(`onboarding_${user.uid}`);
    if (cached === 'true') {
      setHasCompletedOnboarding(true);
      setLoading(false);
      return;
    }

    if (profileLoaded) {
      const completed = !!user.completedOnboarding;
      setHasCompletedOnboarding(completed);
      setLoading(false);
      if (completed) {
        localStorage.setItem(`onboarding_${user.uid}`, 'true');
      }
      return;
    }

    if (!profileFetchLoading && sawProfileFetchRef.current) {
      // The profile fetch settled without a profile — same fallback the old
      // direct fetch used on error.
      setHasCompletedOnboarding(false);
      setLoading(false);
      return;
    }

    setLoading(true);
  }, [user, profileFetchLoading, profileLoaded]);

  const completeOnboarding = async (profile: FamilyProfile) => {
    try {
      const token = await user!.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/family-profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          familyProfile: {
            ...profile,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to save family profile: ${response.status}`);
      }
      
      // Update state and cache
      setHasCompletedOnboarding(true);
      localStorage.setItem(`onboarding_${user!.uid}`, 'true');
      
      // Scroll to top when onboarding is completed
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('Error completing onboarding:', error);
      throw error;
    }
  };

  return (
    <OnboardingContext.Provider value={{
      hasCompletedOnboarding,
      loading,
      completeOnboarding,
    }}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};
